import { Link } from "react-router-dom";
import { ArrowUpRight, Check, MapPin } from "lucide-react";
import { RevealText, Reveal } from "@/components/site/Reveal";
import { IMAGES, ABOUT, LOCATIONS } from "@/lib/assets";

const HIGHLIGHTS = ["Real-Time Tracking", "On-Time Delivery", "Customizable Logistics Solutions", "24/7 Customer Support"];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative pt-40 pb-20 mx-auto max-w-[1600px] px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6">/ About Freight IQ</p>
        <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[13vw] sm:text-[10vw] lg:text-[8vw]">
          <RevealText lines={["About", "Freight IQ"]} testid="page-title" />
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-3xl text-white/70 text-xl leading-relaxed">{ABOUT.intro}</p>
        </Reveal>
      </section>

      <section className="relative h-[70vh] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1685380973823-5f2ca285bf9b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800" alt="Vancouver harbour waterfront with North Shore mountains" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      </section>

      {/* Business Philosophy */}
      <section className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28 grid lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-5">
          <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter leading-tight">Our Business Philosophy</h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7 space-y-6 text-white/60 text-lg">
          {ABOUT.philosophy.map((p, i) => <p key={i}>{p}</p>)}
          <ul className="grid sm:grid-cols-2 gap-3 pt-4">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-center gap-3 text-white/80">
                <Check size={16} className="text-primary shrink-0" /> {h}
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* Values */}
      <section data-testid="values" className="border-y border-white/10 bg-[#080808]">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-24">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">/ Our Values</p>
            <h2 className="font-display text-4xl sm:text-6xl uppercase tracking-tighter leading-[0.9]">What we stand for.</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {ABOUT.values.map((v, i) => (
              <Reveal key={v.t} delay={(i % 3) * 0.06} className="bg-[#080808] p-10">
                <span className="font-display text-5xl stroke-text">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display text-2xl uppercase tracking-tight mt-6">{v.t}</h3>
                <p className="text-white/60 mt-3">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Purpose & Strategy */}
      <section className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28 grid lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-4">
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">/ Purpose & Strategy</p>
          <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter leading-tight sticky top-28">
            Making logistics smarter, more sustainable, more human.
          </h2>
        </Reveal>
        <div className="lg:col-span-8 divide-y divide-white/10">
          {ABOUT.strategy.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.06} className="py-8 grid grid-cols-12 gap-6 items-start">
              <span className="col-span-12 sm:col-span-2 font-display text-4xl stroke-text">{String(i + 1).padStart(2, "0")}</span>
              <div className="col-span-12 sm:col-span-10">
                <h3 className="font-display text-2xl uppercase tracking-tight">{s.t}</h3>
                <p className="text-white/60 mt-2 text-lg">{s.d}</p>
              </div>
            </Reveal>
          ))}
          <Reveal className="py-8">
            <p className="text-white/70 text-lg">At Freight IQ, we're building the future of freight—where flexibility meets reliability, and logistics becomes a competitive edge.</p>
          </Reveal>
        </div>
      </section>

      {/* Locations */}
      <section className="border-t border-white/10 bg-[#080808]">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-24">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">/ Our Locations</p>
            <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter">A global footprint.</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {LOCATIONS.map((loc, i) => (
              <Reveal key={i} delay={(i % 4) * 0.06} className="bg-[#080808] p-8">
                <MapPin size={18} className="text-primary" />
                <p className="font-display text-xl uppercase tracking-tight mt-4">{loc.country}</p>
                <p className="text-white/50 text-sm mt-2">{loc.detail}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28 text-center flex flex-col items-center">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-6xl uppercase tracking-tighter">Move with Freight IQ.</h2>
          <a href="mailto:sales@freight-iq.com?subject=New%20Enquiry%20-%20Freight%20IQ" className="inline-flex items-center gap-2 mt-8 hover-fill px-8 py-4 font-mono text-xs uppercase tracking-widest">
            Connect with us <ArrowUpRight size={16} />
          </a>
        </Reveal>
      </section>
    </div>
  );
}
